import React from 'react';
import { Cloud, CloudOff, RefreshCw, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface SyncStatusIndicatorProps {
  isOnline: boolean;
  pending: number;
  failed: number;
  synced?: number;
  onSync?: () => void;
  className?: string;
}

export function SyncStatusIndicator({
  isOnline,
  pending,
  failed,
  synced: _synced = 0,
  onSync,
  className
}: SyncStatusIndicatorProps) {
  const [isSyncing, setIsSyncing] = React.useState(false);

  const handleSync = async () => {
    if (onSync && !isSyncing) {
      setIsSyncing(true);
      try {
        await onSync();
      } finally {
        setIsSyncing(false);
      }
    }
  };

  // Don't show if everything is synced and online
  if (isOnline && pending === 0 && failed === 0) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Online/Offline Status */}
      <div
        className={cn(
          "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
          isOnline
            ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
            : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
        )}
        title={isOnline ? 'Connected to server' : 'Working offline - changes will sync when reconnected'}
      >
        {isOnline ? (
          <Cloud className="h-3 w-3" />
        ) : (
          <CloudOff className="h-3 w-3" />
        )}
        <span>{isOnline ? 'Online' : 'Offline'}</span>
      </div>

      {/* Pending changes */}
      {pending > 0 && (
        <div
          className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400 text-xs font-medium"
          title={`${pending} expense${pending !== 1 ? 's' : ''} waiting to sync`}
        >
          <RefreshCw className="h-3 w-3" />
          <span>{pending} pending</span>
        </div>
      )}

      {/* Failed syncs */}
      {failed > 0 && (
        <div
          className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-xs font-medium"
          title={`${failed} expense${failed !== 1 ? 's' : ''} failed to sync`}
        >
          <AlertCircle className="h-3 w-3" />
          <span>{failed} failed</span>
        </div>
      )}

      {/* Manual sync button */}
      {isOnline && (pending > 0 || failed > 0) && onSync && (
        <Button
          size="sm"
          variant="ghost"
          onClick={handleSync}
          disabled={isSyncing}
          className="h-7 px-2"
        >
          <RefreshCw className={cn(
            "h-3 w-3",
            isSyncing && "animate-spin"
          )} />
          <span className="ml-1 text-xs">Sync now</span>
        </Button>
      )}
    </div>
  );
}

// Compact version for mobile
export function SyncStatusBadge({
  isOnline,
  pending,
  failed
}: Pick<SyncStatusIndicatorProps, 'isOnline' | 'pending' | 'failed'>) {
  const hasIssues = !isOnline || pending > 0 || failed > 0;

  if (!hasIssues) {
    return null;
  }

  return (
    <div className={cn(
      "inline-flex items-center justify-center w-2 h-2 rounded-full",
      !isOnline ? "bg-gray-400" :
      failed > 0 ? "bg-red-500" :
      pending > 0 ? "bg-amber-500" :
      "bg-green-500"
    )}>
      <span className="sr-only">
        {!isOnline ? 'Offline' :
         failed > 0 ? `${failed} sync failures` :
         pending > 0 ? `${pending} pending` :
         'Synced'}
      </span>
    </div>
  );
}